<template>
  <div>
    <TableModular :rows="rows" :columns="COLUMNS" actions-label="Ações" actions-width="160px">
      <template #column-registro="{ row }">
        <span
          class="text-xs text-neutral50 font-mono"
          :data-testid="`check-validation-id-${row.checkId}`"
        >
          #{{ row.checkId }}
        </span>
      </template>

      <template #column-medico="{ row }">
        <div class="w-full flex justify-start" :data-testid="`check-validation-row-${row.checkId}`">
          <DoctorInfo
            :name="row.doctor.name"
            :subtitle="row.doctor.crmLabel ?? DISPLAY_FALLBACK"
            size="sm"
            truncate
          />
          <v-tooltip
            activator="parent"
            location="top"
            :text="doctorTooltip(row as CheckValidationRow)"
          />
        </div>
      </template>

      <template #column-unidade="{ row }">
        <div class="w-full flex flex-col gap-0.5 overflow-hidden text-left">
          <span class="text-sm font-bold text-textPrimary truncate">
            {{ truncateText(row.unit, UNIT_NAME_MAX_LENGTH) }}
          </span>
          <span class="text-xs text-neutral50 truncate">{{ row.scheduleLabel }}</span>
          <v-tooltip
            activator="parent"
            location="top"
            :text="`${row.unit} · ${row.scheduleLabel}`"
          />
        </div>
      </template>

      <template #column-data="{ row }">
        <span class="text-sm text-textPrimary whitespace-nowrap">{{ row.stationDate }}</span>
      </template>

      <template #column-previsto="{ row }">
        <span class="text-sm text-textPrimary whitespace-nowrap">{{ row.expectedRange }}</span>
      </template>

      <template #column-digitado="{ row }">
        <span class="text-sm text-textPrimary whitespace-nowrap">{{ row.typedRange }}</span>
      </template>

      <template #column-dif="{ row }">
        <span
          class="text-sm whitespace-nowrap"
          :class="row.diffMinutes ? 'font-bold text-textPrimary' : 'text-neutral50'"
          :data-testid="`check-validation-diff-${row.checkId}`"
        >
          {{ row.diffLabel }}
        </span>
      </template>

      <template #column-status="{ row }">
        <div class="w-full flex justify-center">
          <CheckValidationStatusBadge
            :status="row.status"
            :is-overdue="row.isOverdue"
            :is-edited-since-submit="row.isEditedSinceSubmit"
            :is-evidence-unavailable="isEvidenceUnavailable(row as CheckValidationRow)"
          />
        </div>
      </template>

      <template #actions="{ row }">
        <!-- Q27 — com a fila aposentada a linha pendente não some: o botão fica
             desabilitado, porque a tela vira histórico somente leitura. -->
        <OutlineButton
          v-if="isPending(row as CheckValidationRow)"
          text="Avaliar registro"
          width="auto"
          height="32px"
          :disabled="isRetired"
          :data-testid="`check-validation-evaluate-${row.checkId}-button`"
          @click="emit('evaluate', row as CheckValidationRow)"
        />
        <OutlineButton
          v-else
          text="Detalhes"
          width="auto"
          height="32px"
          :data-testid="`check-validation-details-${row.checkId}-button`"
          @click="emit('details', row as CheckValidationRow)"
        />
      </template>
    </TableModular>
  </div>
</template>

<script setup lang="ts">
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import DoctorInfo from '@/components/DoctorInfo.vue'
import { truncateText } from '@/utils/string'
import { CheckValidationStatus } from '@/Network/Types/Responses/CheckValidationResponses'
import CheckValidationStatusBadge from './CheckValidationStatusBadge.vue'
import { COLUMNS, UNIT_NAME_MAX_LENGTH } from '../constants'
import { DISPLAY_FALLBACK } from '../mappers'
import type { CheckValidationRow } from '../types'

withDefaults(
  defineProps<{
    rows: CheckValidationRow[]
    isEvidenceUnavailable: (row: CheckValidationRow) => boolean
    /** Q27 — fila aposentada: desabilita a única ação de decisão da linha. */
    isRetired?: boolean
  }>(),
  { isRetired: false }
)

const emit = defineEmits<{
  evaluate: [row: CheckValidationRow]
  details: [row: CheckValidationRow]
}>()

const isPending = (row: CheckValidationRow) => row.status === CheckValidationStatus.PENDING_REVIEW

const doctorTooltip = (row: CheckValidationRow): string =>
  row.doctor.crmLabel
    ? `${row.doctor.name ?? ''} — ${row.doctor.crmLabel}`
    : (row.doctor.name ?? '')
</script>
